import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock } from "react-icons/fa";
import "./horizontalvoyagecard.css";
import { resolveImageUrl } from "../../constants";

export default function HorizontalVoyageCard({ voyage, duration }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const firstImage = voyage.images?.[0]?.url
    ? resolveImageUrl(voyage.images[0].url)
    : '/placeholder-voyage.jpg';

  const price = voyage.places?.[0]?.price;
  const formattedPrice = price ? Number(price).toLocaleString() : '';

  const title = isRTL && voyage.title_ar ? voyage.title_ar : voyage.title;
  const description = isRTL && voyage.description_ar ? voyage.description_ar : voyage.description;
  const cite = isRTL && voyage.cite_ar ? voyage.cite_ar : voyage.cite;

  // Parse departures to get available dates
  const parseDepartures = (departures) => {
    if (Array.isArray(departures)) return departures;
    if (typeof departures === 'string') {
      try { return JSON.parse(departures); } catch { return []; }
    }
    return [];
  };

  const departures = parseDepartures(voyage.departures);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <motion.div
      className={`horizontal-voyage-card ${isRTL ? 'rtl' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01, boxShadow: "0 10px 25px rgba(0, 0, 0, 0.12)" }}
      transition={{ duration: 0.3 }}
      onClick={() => navigate(`/voyage-org/${voyage.code}`)}
    >
      <div className="hvc-image-container">
        <img
          src={firstImage}
          alt={title}
          className="hvc-img"
          loading="lazy"
        />
      </div>

      <div className="hvc-content">
        <div className="hvc-info">
          <h3 className="hvc-title">{title}</h3>

          <div className="hvc-location">
            <FaMapMarkerAlt className="hvc-icon" />
            <span>{cite}</span>
          </div>

          {description && (
            <p className="hvc-description" dir={isRTL ? 'rtl' : 'ltr'}>{description}</p>
          )}

          <div className="hvc-meta">
            {duration && (
              <div className="hvc-meta-item">
                <FaClock className="hvc-meta-icon" />
                <span>{duration}</span>
              </div>
            )}
          </div>

          {departures.length > 0 && (
            <div className="hvc-dates">
              <FaCalendarAlt className="hvc-meta-icon" />
              <div className="hvc-dates-list">
                {departures.slice(0, 1).map((dep, i) => (
                  <span key={i} className="hvc-date-tag">
                    {formatDate(dep.start)} → {formatDate(dep.end)}
                  </span>
                ))}
                {departures.length > 1 && (
                  <span className="hvc-date-more">+{departures.length - 1}</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="hvc-booking">
          <div className="hvc-price-container">
            <span className="hvc-price-from">{t('carouselcard.from')}</span>
            <div className="hvc-price-value">
              {formattedPrice}
              <span className="hvc-price-unit"> DA</span>
            </div>
            <span className="hvc-price-note">{t('carouselcard.perPerson')}</span>
          </div>

          <button
            className="hvc-reserve-button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/voyage-org/${voyage.code}`);
            }}
          >
            {t('carouselcard.seeOffer')}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
